import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang, FOUNDER_PHOTO } from "../i18n";
import { CheckMark } from "./Logo";

export default function About() {
  const { t } = useLang();
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="about" ref={ref} className="py-24 md:py-40 bg-crema overflow-hidden" data-testid="about-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Photo — clipped editorial frame with spotlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative order-1 lg:order-none"
        >
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-w-md mx-auto lg:mx-0 shadow-[0_40px_80px_-40px_rgba(43,42,40,0.5)]">
            <motion.img
              style={{ y }}
              src={FOUNDER_PHOTO}
              alt={t.about.caption}
              className="absolute inset-0 h-[115%] w-full object-cover"
              data-testid="about-photo"
            />
          </div>
          <div className="absolute -bottom-5 -left-2 lg:-left-6 flex items-center gap-2.5 rounded-full bg-charcoal text-crema px-5 py-3 shadow-xl">
            <CheckMark size={20} box="#FAF6F0" accent="#0F6E56" />
            <span className="text-sm font-semibold">{t.about.caption}</span>
          </div>
        </motion.div>

        <div>
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-coral mb-6"
            data-testid="about-chapter"
          >
            {t.about.chapter}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-black tracking-tight text-charcoal text-3xl md:text-5xl leading-[1.05]"
            data-testid="about-headline"
          >
            {t.about.headline}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="mt-8 text-lg md:text-xl leading-relaxed text-warmgray max-w-xl"
            data-testid="about-body"
          >
            {(() => {
              const parts = t.about.body.split("He Meant To");
              return parts.map((part, i) => (
                <React.Fragment key={i}>
                  {part}
                  {i < parts.length - 1 && (
                    <span className="font-display font-bold lowercase text-teal">he meant to</span>
                  )}
                </React.Fragment>
              ));
            })()}
          </motion.p>
        </div>
      </div>
    </section>
  );
}
